<template>
  <section
    :class="[
      'wrapper',
      'content',
      'content--centered',
      $arMediaQuery.pageContent.maxWidth('sm') && 'sm-max',
      $arMediaQuery.pageContent.maxWidth('xs') && 'xs-max',
    ]">
    <am2-step-link
      class="step-back-link"
      text="Back to integrations"
      @click="handleBackLinkClick" />
    <div
      :class="[
        'title-section',
        $arMediaQuery.pageContent.maxWidth('md') && 'md-max',
      ]">
      <div class="title-section-left">
        <am2-icon-title-section
          title="Patreon"
          description="Automatically sync your members from Patreon"
          :icon-props="{
            name: 'patreon',
            color: $arStyle.color.patreon,
            height: '46px',
            width: '40px',
          }"
        />
      </div>
      <div class="title-section-right" v-if="!isFetchingPatreonIntegration && integrationsSummary">
        <am2-integration-account-select
          v-if="!isFetchingPatreonIntegration && patreonIntegration && currentIntegration"
          :default-selected-integration-oid="currentIntegration.oid"
          :available-integrations="integrationsSummary"
          @select="handleIntegrationChange"
          @new-account="handleAddNewIntegration"
          :style="{ marginRight: '10px', maxWidth: 'calc(100vw - 80px)', }"
        />
        <am2-expand-button-dropdown
          align="left"
          :button-props="{ iconName: 'settings' }"
          :items="[
            {
              name: 'Open Patreon',
              value: 'goToPatreonSite',
            },
            {
              name: 'Reconnect Integration',
              value: 'reconnectIntegration',
            },
            {
              name: 'Remove Account',
              value: 'removeIntegration',
            },
          ]"
          @select="handleSettingOptionSelect" />
      </div>
    </div>


    <section v-if="isAddingPatreonIntegration || isFetchingPatreonIntegration" class="integration-loading-container">
      <am2-loading-bubble />
    </section>
    <section v-else>
      <section v-if="integrationFailed" class="integration-failed-message">
        <ar-snackbar
          type="warning"
          message="Your Patreon connection is no longer valid. This means we can't sync your member data. <a>Please reconnect your account</a>"
          @anchorClick="handleReconnectIntegration"
          :style="{ width: '100%' }"
        />
      </section>

      <SyncBlockContainer
        v-else
        class="sync-block-container"
        :is-fetching-sync-task="isFetchingSyncTask"
        :sync-status="syncStatus"
        :sync-time="syncTime"
        :sync-copies="{
          'in-progress': 'Syncing data from Patreon',
          'pending': 'The data sync is still pending',
          'completed': 'Your Patreon account is currently connected',
          'cancelled': 'Your Patreon account connection is cancelled',
          'failed': this.syncErrorMessage,
          'default': 'Syncing tasks not found',
        }"
        @startSync="handleStartSync"
        @stopSync="handleStopSync"
      />
    </section>

  </section>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import SyncBlockContainer from '../components/sync-block-container';

export default {
  name: 'PatreonIntegration',
  layout: 'default',
  components: {
    SyncBlockContainer,
  },

  data: () => ({
    currentlySelectedIntegrationIndex: 0,
    syncStatusPoller: null,
  }),

  computed: {
    ...mapState({
      patreonIntegration: state => state.patreonIntegration.integration,
      isFetchingPatreonIntegration: state => state.patreonIntegration.isFetchingIntegration,
      isAddingPatreonIntegration: state => state.patreonIntegration.isAddingIntegration,
      isDeletingPatreonIntegration: state => state.patreonIntegration.isDeletingIntegration,
      isFetchingSyncTask: state => state.patreonIntegration.isFetchingSyncTask,
      syncTask: state => state.patreonIntegration.syncTask,
      syncStatus: state => state.patreonIntegration.syncStatus,
    }),

    patreonIntegrationEnabled() {
      return process.env.arEnablePatreonIntegration;
    },

    syncErrorMessage() {
      return this.syncTask?.statusDetails?.lastError || null;
    },

    syncTime() {
      return this.syncTask?.sysMtime || null;
    },

    integrationsSummary() {
      if (!this.patreonIntegration) return null;
      return this.patreonIntegration
        .filter(i => i.status !== 'failed')
        .map(i => ({
          oid: i.oid,
          name: i.integration?.user?.attributes?.email,
          email: i.integration?.user?.attributes?.email,
          imageSrc: i.integration?.user?.attributes?.imageUrl,
        }));
    },

    currentIntegration() {
      if (this.currentlySelectedIntegrationIndex === null) return null;
      if (!this.patreonIntegration || this.patreonIntegration.length < 1 || !this.patreonIntegration[this.currentlySelectedIntegrationIndex]) return null;
      return this.patreonIntegration[this.currentlySelectedIntegrationIndex];
    },

    integrationFailed() {
      if (!this.currentIntegration) return false;
      return this.currentIntegration.status === 'failed';
    },
  },

  async mounted() {
    if (!this.patreonIntegrationEnabled) {
      this.handleBackLinkClick();
    }
    await this.fetchIntegration();
    this.startFetchTasksPolling();
  },

  beforeDestroy() {
    this['patreonIntegration/RESET_INTEGRATION']();

    if (this.syncStatusPoller) {
      clearInterval(this.syncStatusPoller);
    }
  },

  methods: {
    ...mapActions([
      'SHOW_CONFIRM',
      'patreonIntegration/FETCH_INTEGRATION',
      'patreonIntegration/DELETE_INTEGRATION',
      'patreonIntegration/SYNC_START',
      'patreonIntegration/SYNC_STOP',
      'patreonIntegration/FETCH_SYNC_TASK',
      'CONNECT_TO_INTEGRATION',
      'promoterTasks/START_POLLING_PENDING_TASKS',
    ]),
    ...mapMutations([
      'patreonIntegration/RESET_INTEGRATION',
    ]),
    startFetchTasksPolling() {
      if (this.currentIntegration?.oid && !this.isFetchingSyncTask) {
        this.fetchTasks();
        this.syncStatusPoller = setInterval(this.fetchTasks, 3000);
      }
    },
    async fetchIntegration() {
      await this['patreonIntegration/FETCH_INTEGRATION']();
      if (this.patreonIntegration.length === 0) {
        this.$router.push({
          path: '/settings/integrations',
        });
      }
    },

    async fetchTasks() {
      await this['patreonIntegration/FETCH_SYNC_TASK'](this.currentIntegration.oid);

      if (this.currentIntegration && this.hasExistingTask && this.syncStatus !== "in-progress") {
        window.clearInterval(this.syncStatusPoller);
        this.syncStatusPoller = null;
        this['promoterTasks/START_POLLING_PENDING_TASKS']({});
      }
    },

    async connectToPatreon() {
      try {
        await this.CONNECT_TO_INTEGRATION({
          provider: 'patreon',
          app: 'patreon'
        });
        await this.fetchIntegration();
        this.currentlySelectedIntegrationIndex = this.patreonIntegration.length - 1;
        this.startFetchTasksPolling();
      } catch (e) {
        // If it's closed by user, then it's all good
        if (e.name === 'USER_CLOSE') {
          return;
        }

        this.$arNotification.push({ type: 'error', message: `Failed to connect to Patreon: ${e.message}` });
      }
    },

    hasExistingTask() {
      return this.syncTask !== null;
    },

    handleBackLinkClick() {
      this.$router.push({ path: '/settings/integrations' });
    },

    handleIntegrationChange(integration, index) {
      this.currentlySelectedIntegrationIndex = index;
      this.fetchTasks();
    },

    async handleAddNewIntegration() {
      this.connectToPatreon();
    },

    async handleStartSync() {
      setTimeout(() => {
        this['promoterTasks/START_POLLING_PENDING_TASKS']({});
      }, 750);
      await this['patreonIntegration/SYNC_START']({ oid: this.currentIntegration.oid });
      if (this.syncStatus !== 'completed') {
        this.syncStatusPoller = setInterval(this.fetchTasks, 3000);
      }
    },

    handleStopSync() {
      this['patreonIntegration/SYNC_STOP']({oid: this.currentIntegration.oid});
      setTimeout(() => {
        this['promoterTasks/START_POLLING_PENDING_TASKS']({});
      }, 750);
    },

    async handleReconnectIntegration() {
      const agreed = await this.SHOW_CONFIRM({
        messageHtml: `Make sure you are logging in with the Patreon account <strong>${this.currentIntegration.integration.user.attributes.email}</strong> on Patreon before reconnecting it.`,
        confirmButtonText: 'Reconnect account',
      });
      if (!agreed) {
        return;
      }

      const succeed = await this['patreonIntegration/DELETE_INTEGRATION'](this.currentIntegration.oid);
      if (!succeed) {
        return;
      }

      this.connectToPatreon();
    },

    async handleSettingOptionSelect(item) {
      if (item.value === 'removeIntegration') {
        const answer = await this.SHOW_CONFIRM({
          messageHtml: 'Removing the Patreon integration will stop syncing your orders and customer data from Patreon.',
          confirmButtonText: 'Remove integration',
          iconName: 'alert-question-mark',
          title: 'Are you sure?',
        });
        if (answer) {
          try {
            await this['patreonIntegration/DELETE_INTEGRATION'](this.currentIntegration.oid);
            if (this.patreonIntegration.length > 0) {
              this.currentlySelectedIntegrationIndex = 0;
              await this.fetchIntegration();
              this.startFetchTasksPolling();
            } else {
              this.$router.push({ path: '/settings/integrations' });
            }
          } catch (e) {
            this.$arNotification.push({ type: 'error', message: 'Failed to remove integration' });
          }
        }
      } else if (item.value === 'reconnectIntegration') {
        this.handleReconnectIntegration();
      } else if (item.value === 'goToPatreonSite') {
        if (!this.currentIntegration) return;
        var win = window.open('https://www.patreon.com/creator-home', '_blank');
        win.focus();
      }
    },
  }
};
</script>

<style lang="scss" scoped>
.wrapper {

  &.sm-max {
    padding: 0 24px;
  }
  &.xs-max {
    padding: 0 12px;
  }

  .integration-loading-container {
    display: flex;
    justify-content: center;
    margin-top: 65px;
  }


  .step-back-link {
    margin-top: 48px;
  }

  .title-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 48px;

    .title-section-left {
      display: inline-flex;
      align-items: flex-start;

      .tag {
        position: relative;
        margin-left: 10px;
        top: 4px;
      }
    }

    .title-section-right {
      display: inline-flex;
      align-items: center;

      .select {
        width: 300px;
        margin-right: 10px;
      }
    }

    &.md-max {
      flex-direction: column;
      align-items: flex-start;

      .title-section-right {
        margin-top:16px;
      }
    }
  }


  .sync-block-container {
    margin-top:64px;
  }
}
</style>
